// src/modules/hr/pages/AttendanceManagement.jsx — Phase 18
import React, { useEffect, useState, useCallback } from 'react';
import {
  Card, Table, Typography, Row, Col, Button, Select, Tag, Tooltip, Modal, Form,
  DatePicker, Input, message, Space, Statistic, Tabs,
} from 'antd';
import { ClockCircleOutlined, ReloadOutlined, CheckCircleOutlined, PlusOutlined, FieldTimeOutlined } from '@ant-design/icons';
import dayjs from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { usePermissions } from '../../../hooks/usePermissions';
import { useAuth } from '../../../context/AuthContext';
import { fetchEmployees, fetchAttendance, markAttendance, checkInAttendance, fetchMonthlySummary, fetchLateArrivals, actOnLateArrival } from '../api';
import { ATTENDANCE_STATUS_OPTIONS, LATE_ARRIVAL_STATUS_OPTIONS, tagColor, tagLabel } from '../constants/hrConstants';

const { Title, Text } = Typography;

// ?tab=late lets approval deep links land on the Late Arrival queue.
const ATTENDANCE_TABS = ['daily', 'summary', 'late'];

export default function AttendanceManagement() {
  const [searchParams] = useSearchParams();
  const { can } = usePermissions();
  const { user } = useAuth();
  const [employees, setEmployees] = useState([]);
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [filters, setFilters] = useState({ date: dayjs(), employeeId: null });

  const [markOpen, setMarkOpen] = useState(false);
  const [markForm] = Form.useForm();
  const [saving, setSaving] = useState(false);

  const [summary, setSummary] = useState(null);
  const [summaryParams, setSummaryParams] = useState({ employeeId: null, period: dayjs() });

  // Late Arrival regularization queue
  const [lateData, setLateData] = useState([]);
  const [lateLoading, setLateLoading] = useState(false);
  const [lateStatus, setLateStatus] = useState('PENDING');
  const [actingId, setActingId] = useState(null);

  useEffect(() => { fetchEmployees({ limit: 1000 }).then((r) => setEmployees(r?.data?.data || [])).catch(() => {}); }, []);

  const loadLate = useCallback(async () => {
    setLateLoading(true);
    try {
      const params = {};
      if (lateStatus) params.lateArrivalStatus = lateStatus;
      const res = await fetchLateArrivals(params);
      setLateData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load late arrivals'); }
    finally { setLateLoading(false); }
  }, [lateStatus]);

  useEffect(() => { loadLate(); }, [loadLate]);

  const decideLate = async (record, approve) => {
    setActingId(record._id);
    try {
      await actOnLateArrival(record._id, { action: approve ? 'approve' : 'reject' });
      message.success(`Late arrival ${approve ? 'approved — marked Present' : 'rejected — marked Half Day'}`);
      loadLate();
      load();
    } catch (err) {
      message.error(err?.response?.data?.message || 'Action failed');
    } finally { setActingId(null); }
  };

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const params = { limit: 200 };
      if (filters.date) params.date = filters.date.format('YYYY-MM-DD');
      if (filters.employeeId) params.employeeId = filters.employeeId;
      const res = await fetchAttendance(params);
      setData(res?.data?.data || []);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed to load attendance'); }
    finally { setLoading(false); }
  }, [filters]);

  useEffect(() => { load(); }, [load]);

  const submitMark = async () => {
    try {
      const v = await markForm.validateFields();
      setSaving(true);
      await markAttendance({ ...v, date: v.date.format('YYYY-MM-DD'), checkIn: v.checkIn?.toISOString(), checkOut: v.checkOut?.toISOString() });
      message.success('Attendance marked'); setMarkOpen(false); markForm.resetFields(); load();
    } catch (err) { if (!err?.errorFields) message.error(err?.response?.data?.message || 'Failed'); }
    finally { setSaving(false); }
  };

  const selfCheckIn = async () => {
    try { await checkInAttendance({}); message.success('Checked in'); load(); }
    catch (err) { message.error(err?.response?.data?.message || 'Check-in failed'); }
  };

  const loadSummary = async () => {
    if (!summaryParams.employeeId) return message.warning('Select an employee');
    try {
      const res = await fetchMonthlySummary({ employeeId: summaryParams.employeeId, year: summaryParams.period.year(), month: summaryParams.period.month() + 1 });
      setSummary(res?.data?.data || null);
    } catch (err) { message.error(err?.response?.data?.message || 'Failed'); }
  };

  const columns = [
    { title: 'Date', dataIndex: 'date', width: 120, render: (v) => dayjs(v).format('DD/MM/YYYY') },
    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
    { title: 'Code', dataIndex: ['employee', 'employeeCode'], width: 100, render: (v, r) => r.employee?.employeeCode || '—' },
    { title: 'Status', dataIndex: 'status', width: 110, render: (v) => <Tag color={tagColor(ATTENDANCE_STATUS_OPTIONS, v)}>{tagLabel(ATTENDANCE_STATUS_OPTIONS, v)}</Tag> },
    { title: 'In', dataIndex: 'checkIn', width: 90, render: (v) => v ? dayjs(v).format('HH:mm') : '—' },
    { title: 'Out', dataIndex: 'checkOut', width: 90, render: (v) => v ? dayjs(v).format('HH:mm') : '—' },
    { title: 'Hours', dataIndex: 'workedHours', width: 80, render: (v) => v || 0 },
    { title: 'Source', dataIndex: 'source', width: 100, render: (v) => <Tag>{v}</Tag> },
  ];

  const s = summary?.summary || {};

  return (
    <div style={{ padding: '16px 0' }}>
      <Row justify="space-between" align="middle" style={{ marginBottom: 16 }}>
        <Col>
          <Title level={5} style={{ margin: 0 }}><ClockCircleOutlined style={{ marginRight: 8 }} />Attendance</Title>
          <Text type="secondary" style={{ fontSize: 12 }}>Manual · biometric · geo · mobile</Text>
        </Col>
        <Col>
          <Space>
            <Button icon={<CheckCircleOutlined />} onClick={selfCheckIn}>Check In (me)</Button>
            {can('hrAttendance', 'edit') && <Button type="primary" icon={<PlusOutlined />} onClick={() => { markForm.setFieldsValue({ date: dayjs(), status: 'PRESENT' }); setMarkOpen(true); }}>Mark</Button>}
          </Space>
        </Col>
      </Row>

      <Tabs
        defaultActiveKey={ATTENDANCE_TABS.includes(searchParams.get('tab')) ? searchParams.get('tab') : 'daily'}
        items={[
          {
            key: 'daily', label: 'Daily Register',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 8]} align="middle">
                    <Col xs={12} sm={6}><DatePicker style={{ width: '100%' }} value={filters.date} onChange={(d) => setFilters((f) => ({ ...f, date: d }))} /></Col>
                    <Col xs={12} sm={8}><Select allowClear showSearch optionFilterProp="children" style={{ width: '100%' }} placeholder="Employee" value={filters.employeeId} onChange={(v) => setFilters((f) => ({ ...f, employeeId: v || null }))}>{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Col>
                    <Col><Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={load} /></Tooltip></Col>
                  </Row>
                </Card>
                <Table rowKey="_id" columns={columns} dataSource={data} loading={loading} size="small" scroll={{ x: 900 }} pagination={{ pageSize: 50 }} />
              </>
            ),
          },
          {
            key: 'summary', label: 'Monthly Summary',
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 8]} align="middle">
                    <Col xs={12} sm={8}><Select showSearch optionFilterProp="children" style={{ width: '100%' }} placeholder="Employee" value={summaryParams.employeeId} onChange={(v) => setSummaryParams((p) => ({ ...p, employeeId: v }))}>{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Col>
                    <Col xs={12} sm={6}><DatePicker picker="month" style={{ width: '100%' }} value={summaryParams.period} onChange={(d) => setSummaryParams((p) => ({ ...p, period: d || dayjs() }))} /></Col>
                    <Col><Button type="primary" onClick={loadSummary}>Load</Button></Col>
                  </Row>
                </Card>
                {summary && (
                  <Row gutter={[12, 12]}>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Present" value={s.present || 0} valueStyle={{ color: '#389e0d' }} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Absent" value={s.absent || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Leave" value={s.leave || 0} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Holiday" value={s.holiday || 0} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Weekly Off" value={s.weeklyOff || 0} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Late" value={s.lateMarks || 0} valueStyle={{ color: '#fa8c16' }} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="OT Hours" value={s.overtimeHours || 0} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="LWP" value={s.lwp || 0} valueStyle={{ color: '#cf1322' }} /></Card></Col>
                    <Col xs={8} sm={4}><Card size="small"><Statistic title="Payable Days" value={s.payableDays || 0} valueStyle={{ color: '#1677ff' }} /></Card></Col>
                  </Row>
                )}
              </>
            ),
          },
          {
            key: 'late', label: <span><FieldTimeOutlined /> Late Arrival</span>,
            children: (
              <>
                <Card size="small" style={{ marginBottom: 16 }}>
                  <Row gutter={[12, 8]} align="middle">
                    <Col xs={16} sm={8}>
                      <Select style={{ width: '100%' }} value={lateStatus} onChange={setLateStatus}
                        options={[{ label: 'Pending', value: 'PENDING' }, { label: 'Approved', value: 'APPROVED' }, { label: 'Rejected', value: 'REJECTED' }, { label: 'All', value: '' }]} />
                    </Col>
                    <Col><Tooltip title="Refresh"><Button icon={<ReloadOutlined />} onClick={loadLate} /></Tooltip></Col>
                  </Row>
                </Card>
                <Table
                  rowKey="_id"
                  loading={lateLoading}
                  dataSource={lateData}
                  size="small"
                  scroll={{ x: 900 }}
                  pagination={{ pageSize: 50 }}
                  columns={[
                    { title: 'Date', dataIndex: 'date', width: 110, render: (v) => dayjs(v).format('DD/MM/YYYY') },
                    { title: 'Employee', dataIndex: ['employee', 'name'], render: (v, r) => r.employee?.name || '—' },
                    { title: 'Code', dataIndex: ['employee', 'employeeCode'], width: 90, render: (v, r) => r.employee?.employeeCode || '—' },
                    { title: 'Check In', dataIndex: 'checkIn', width: 90, render: (v) => v ? dayjs(v).format('HH:mm') : '—' },
                    { title: 'Late (min)', dataIndex: 'lateMinutes', width: 90, render: (v) => <Text type="warning">{v || 0}</Text> },
                    { title: 'With', dataIndex: 'currentStepName', width: 130, render: (v, r) => r.lateArrivalStatus === 'PENDING' ? (v || 'Approver') : (r.regularizedBy?.name || '—') },
                    { title: 'Status', dataIndex: 'lateArrivalStatus', width: 110, render: (v) => <Tag color={tagColor(LATE_ARRIVAL_STATUS_OPTIONS, v)}>{tagLabel(LATE_ARRIVAL_STATUS_OPTIONS, v)}</Tag> },
                    {
                      title: 'Action', key: 'action', width: 170, fixed: 'right',
                      render: (_, r) => r.lateArrivalStatus === 'PENDING' ? (
                        <Space>
                          <Button size="small" type="primary" loading={actingId === r._id} onClick={() => decideLate(r, true)}>Approve</Button>
                          <Button size="small" danger loading={actingId === r._id} onClick={() => decideLate(r, false)}>Reject</Button>
                        </Space>
                      ) : <Text type="secondary" style={{ fontSize: 12 }}>{r.regularizedAt ? dayjs(r.regularizedAt).format('DD/MM HH:mm') : '—'}</Text>,
                    },
                  ]}
                  locale={{ emptyText: 'No late arrivals' }}
                />
              </>
            ),
          },
        ]}
      />

      <Modal title="Mark Attendance" open={markOpen} onCancel={() => setMarkOpen(false)} onOk={submitMark} okButtonProps={{ loading: saving }} destroyOnClose>
        <Form form={markForm} layout="vertical">
          <Form.Item name="employeeId" label="Employee" rules={[{ required: true }]}><Select showSearch optionFilterProp="children">{employees.map((e) => <Select.Option key={e._id} value={e._id}>{e.name}</Select.Option>)}</Select></Form.Item>
          <Row gutter={12}>
            <Col span={12}><Form.Item name="date" label="Date" rules={[{ required: true }]}><DatePicker style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="status" label="Status" rules={[{ required: true }]}><Select options={ATTENDANCE_STATUS_OPTIONS} /></Form.Item></Col>
            <Col span={12}><Form.Item name="checkIn" label="Check In"><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="checkOut" label="Check Out"><DatePicker showTime style={{ width: '100%' }} /></Form.Item></Col>
            <Col span={12}><Form.Item name="lateMinutes" label="Late (min)"><Input type="number" /></Form.Item></Col>
            <Col span={12}><Form.Item name="overtimeHours" label="OT (hrs)"><Input type="number" /></Form.Item></Col>
          </Row>
          <Form.Item name="remarks" label="Remarks"><Input.TextArea rows={2} /></Form.Item>
        </Form>
      </Modal>
    </div>
  );
}
